/* Rose Lane — product gallery videos + immersive player.
   Loaded by main-product when the section setting rl_video_story is on.

   <rl-gallery-video>  muted, looped playback of a gallery slide while it is on
                       screen; sound / pause buttons; a tap opens the player.
   <rl-gallery-nav>    dots (▶ for videos) under the mobile slider, prev / next
                       over the desktop frame. On phones it mounts Swiper on
                       the theme's slider (see initSwiper).
   <rl-video-story>    full-screen player of the product's videos (Instagram
                       stories navigation, Swiper cube effect), opened by any
                       [data-rl-story-open] whose aria-controls targets it.

   The theme replaces gallery slides on variant change: every element reads the
   DOM when it needs it and never keeps a reference to a slide. */

(() => {
  if (customElements.get('rl-video-story')) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobile = window.matchMedia('(max-width: 749px)');
  const touch = window.matchMedia('(pointer: coarse)');
  // iPhone / iPod, and iPad (which presents itself as a Mac with touch).
  const iOS =
    /iP(hone|od|ad)/.test(navigator.userAgent) ||
    /iP(hone|od|ad)/.test(navigator.platform) ||
    (/Mac/.test(navigator.platform) && navigator.maxTouchPoints > 1);
  let storyOpen = false;

  // Liquid's video.sources only lists the 1080p MP4; the CDN serves the 720p
  // rendition beside it under the same name, at 0.625 × its bitrate (4.8 → 3.0,
  // 7.2 → 4.5 Mbps). Any error falls back to the original file once.
  const lighterRendition = (src) =>
    src.replace(/\.HD-1080p-([\d.]+)Mbps-/, (match, mbps) => '.HD-720p-' + (Number(mbps) * 0.625).toFixed(1) + 'Mbps-');

  // Our own encodes (rl-lazy-video data-rl-*): light or high, in the first
  // codec the browser plays — AV1, then HEVC, then H.264.
  const ENCODED_CODECS = [
    ['Av1', 'video/mp4; codecs="av01.0.08M.08"'],
    ['Hevc', 'video/mp4; codecs="hvc1.1.6.L123.B0"'],
    ['H264', 'video/mp4; codecs="avc1.640028"'],
  ];
  // canPlayType says yes to AV1 wherever a software decoder ships (Chrome and
  // Firefox on any Android phone): a looping 1080p AV1 would then drop frames and
  // drain the battery. Media Capabilities tells hardware from software; until it
  // answers, or where it is missing, AV1 is used as canPlayType says.
  let av1Efficient = null;
  navigator.mediaCapabilities
    ?.decodingInfo({ type: 'file', video: { contentType: ENCODED_CODECS[0][1], width: 1080, height: 1350, bitrate: 4000000, framerate: 30 } })
    .then((info) => (av1Efficient = info.supported && info.powerEfficient && info.smooth))
    .catch(() => {});
  const encodedSource = (video, light) => {
    const version = light ? 'rlLight' : 'rlHigh';
    for (const [codec, type] of ENCODED_CODECS) {
      if (codec === 'Av1' && av1Efficient === false) continue;
      const url = video.dataset[version + codec];
      if (url && video.canPlayType(type) !== '') return url;
    }
    return null;
  };

  // The connection picks the version of our encodes (the gallery's 4:5 or the
  // player's 9:16, each with its own files):
  //   slow (< 6 Mbit/s, or Save-Data / 2G-3G): light everywhere — the high one
  //     (up to ~5 Mbit/s) would stall;
  //   otherwise: light on phones (gallery and player), high elsewhere. Phones
  //     stay light even on a fast reading: on 4G the page's first downloads
  //     come in bursts that read fast, and the high file then starts late — in
  //     the player too, where a fast tap through stories stalled (iPhone 11 on
  //     4G, 2026-10-13); at a phone's size the two look alike (blind test,
  //     2026-10-12).
  // Safari has no Network Information API, so the rate is measured on what the
  // page already downloaded: files whose whole body came over the network (a
  // cache hit or another site's file reads 0, a revalidated one — 304 — only
  // its headers, so both are left out). Downloads that
  // overlap share the line, so they are merged into stretches of time, each
  // one's bytes over its length; the best stretch of 300 KB or more counts.
  // An observer keeps the record: the timing buffer (250 entries) fills up on
  // pages with many apps, and later downloads, the videos included, drop out.
  const SLOW_MBPS = 6;
  const downloads = [];
  try {
    new PerformanceObserver((list) => downloads.push(...list.getEntries())).observe({ type: 'resource', buffered: true });
  } catch {
    downloads.push(...performance.getEntriesByType('resource'));
  }
  const networkSpeed = () => {
    const connection = navigator.connection;
    if (connection?.saveData) return 'slow';
    if (connection && ['slow-2g', '2g', '3g'].includes(connection.effectiveType)) return 'slow';
    const transfers = downloads
      .filter((entry) => entry.encodedBodySize > 0 && entry.transferSize >= entry.encodedBodySize && entry.responseEnd > entry.responseStart)
      .sort((x, y) => x.responseStart - y.responseStart);
    const stretches = [];
    for (const entry of transfers) {
      const last = stretches[stretches.length - 1];
      if (last && entry.responseStart <= last.end) {
        last.end = Math.max(last.end, entry.responseEnd);
        last.bytes += entry.transferSize;
      } else {
        stretches.push({ start: entry.responseStart, end: entry.responseEnd, bytes: entry.transferSize });
      }
    }
    const rates = stretches
      .filter((stretch) => stretch.bytes >= 300000)
      .map((stretch) => (stretch.bytes * 8) / ((stretch.end - stretch.start) * 1000));
    if (!rates.length) return 'unknown';
    const mbps = Math.max(...rates);
    return mbps < SLOW_MBPS ? 'slow' : 'fine';
  };

  // The file a video plays: our encode for the place and the codec, or the
  // Shopify one (its 720p rendition when light).
  const sourceFor = (video, light) => {
    const src = video.dataset.src;
    if (networkSpeed() === 'slow') light = true;
    return encodedSource(video, light) || (light ? lighterRendition(src) : src);
  };

  // Phones, setting rl_video_preload « full »: the next video (gallery slide,
  // story) is downloaded whole with fetch() and played from memory. iOS
  // Safari's own preload stops after the first ~180 KB until the video plays
  // (a cancelled request, the rest at play time); a plain fetch has no such
  // limit. One file ahead at a time: a newer one cancels it, unless its video
  // is already wanted; a video played before its file arrived waits for it
  // (already under way, it ends sooner than a fresh load) — never twice. Not
  // with Save-Data or on 2G / 3G.
  const prefetched = new WeakMap();
  let pendingPrefetch = null;
  const fetchAhead = (video, light) => {
    if (!mobile.matches || !document.querySelector('[data-rl-video-preload="full"]')) return false;
    const connection = navigator.connection;
    if (connection?.saveData || ['slow-2g', '2g', '3g'].includes(connection?.effectiveType)) return false;
    if (!video || video.getAttribute('src') || !video.dataset.src) return false;
    if (prefetched.has(video)) return true;
    if (pendingPrefetch && !pendingPrefetch.blobUrl && !pendingPrefetch.wanted) dropPrefetch(pendingPrefetch.video);
    const entry = { video, url: sourceFor(video, light), controller: new AbortController(), blobUrl: null, wanted: false };
    prefetched.set(video, entry);
    pendingPrefetch = entry;
    entry.ready = fetch(entry.url, { signal: entry.controller.signal, credentials: 'omit' })
      .then((response) => (response.ok ? response.blob() : Promise.reject(new Error(response.status))))
      .then((blob) => (entry.blobUrl = URL.createObjectURL(blob)));
    entry.ready.catch(() => prefetched.get(video) === entry && prefetched.delete(video));
    return true;
  };
  // A prefetch no longer needed (a story left before its file arrived).
  const dropPrefetch = (video) => {
    const entry = video && prefetched.get(video);
    if (!entry) return;
    prefetched.delete(video);
    if (entry.blobUrl) URL.revokeObjectURL(entry.blobUrl);
    else entry.controller.abort();
  };

  // Plays a prefetched file from memory; should it fail, the network file,
  // then Shopify's.
  const playFromMemory = (video, entry, src) => {
    video.addEventListener(
      'error',
      () => {
        URL.revokeObjectURL(entry.blobUrl);
        video.addEventListener('error', () => (video.src = src), { once: true });
        video.src = entry.url;
        video.play().catch(() => {});
      },
      { once: true }
    );
    video.src = entry.blobUrl;
  };

  // rl-lazy-video keeps the file in data-src until it is needed.
  const loadSource = (video, preload, light) => {
    if (video.getAttribute('src') || !video.dataset.src) return;
    const src = video.dataset.src;
    // A video loaded to play now is wanted whole (it loops): iOS Safari then
    // fetches it in one request, instead of a first one it cancels and a
    // second for the rest — one more round trip before the picture.
    video.preload = preload || 'auto';
    const ahead = prefetched.get(video);
    if (ahead?.blobUrl) {
      prefetched.delete(video);
      playFromMemory(video, ahead, src);
      return;
    }
    if (ahead) {
      // Still downloading: wait for it, then play if still wanted; should it
      // fail, the usual load.
      ahead.wanted = true;
      ahead.ready.then(
        () => {
          if (prefetched.get(video) !== ahead) return;
          prefetched.delete(video);
          const wanted = !video.paused;
          playFromMemory(video, ahead, src);
          if (wanted) video.play().catch(() => {});
        },
        () => {
          if (video.getAttribute('src')) return;
          const wanted = !video.paused;
          loadSource(video, preload, light);
          if (wanted) video.play().catch(() => {});
        }
      );
      return;
    }
    const lighter = sourceFor(video, light);
    if (lighter !== src) {
      video.addEventListener(
        'error',
        () => {
          video.src = src;
          video.play().catch(() => {});
        },
        { once: true }
      );
    }
    video.src = lighter;
  };

  // iOS Low Power Mode blocks autoplay, even muted: the next touch or click
  // (a user gesture) starts the gallery videos on screen.
  let retryArmed = false;
  const retryOnGesture = () => {
    if (retryArmed) return;
    retryArmed = true;
    const retry = () => {
      retryArmed = false;
      document.removeEventListener('touchend', retry, true);
      document.removeEventListener('click', retry, true);
      document.dispatchEvent(new CustomEvent('rl-video-story:toggle'));
    };
    document.addEventListener('touchend', retry, true);
    document.addEventListener('click', retry, true);
  };

  const play = (video, light) => {
    loadSource(video, null, light);
    return video.play().catch((error) => {
      if (error?.name === 'NotAllowedError') retryOnGesture();
    });
  };

  const formatTime = (seconds) => {
    const t = Math.max(0, Math.floor(seconds || 0));
    return Math.floor(t / 60) + ':' + String(t % 60).padStart(2, '0');
  };

  const setProgress = (element, ratio) => {
    if (element) element.style.setProperty('--rl-progress', Math.min(1, Math.max(0, ratio || 0)));
  };

  const galleryVideoIds = () =>
    Array.from(document.querySelectorAll('media-gallery rl-gallery-video'), (element) => element.dataset.mediaId);

  // « Voir l'application · [count] vidéos »: counts the videos the gallery shows;
  // the circles style keeps the first three of them.
  const updateChips = () => {
    const ids = galleryVideoIds();
    document.querySelectorAll('[data-rl-video-circles]').forEach((circles) => {
      const items = Array.from(circles.querySelectorAll('[data-rl-story-open]'));
      const shown = items.filter((item) => !ids.length || ids.includes(item.dataset.rlStoryOpen)).slice(0, 3);
      items.forEach((item) => (item.hidden = !shown.includes(item)));
    });
    const count = ids.length;
    if (!count) return;
    document.querySelectorAll('[data-rl-chip-text]').forEach((element) => {
      const template = count === 1 ? element.dataset.one : element.dataset.many;
      element.textContent = template.replace('[count]', count);
    });
  };

  // Shared markup of rl-video-controls: aria-pressed drives the icon shown.
  const syncControls = (root, muted, paused) => {
    const mute = root.querySelector('[data-rl-video-mute]');
    const pause = root.querySelector('[data-rl-video-pause]');
    if (mute) {
      mute.setAttribute('aria-pressed', String(muted));
      mute.setAttribute('aria-label', muted ? mute.dataset.labelOn : mute.dataset.labelOff);
    }
    if (pause) {
      pause.setAttribute('aria-pressed', String(paused));
      pause.setAttribute('aria-label', paused ? pause.dataset.labelPlay : pause.dataset.labelPause);
    }
  };

  class RlGalleryVideo extends HTMLElement {
    constructor() {
      super();
      this.userPaused = reducedMotion.matches;
      this.visible = false;
      this.onTime = () => setProgress(this.bar, this.video.currentTime / this.video.duration);
      this.onPlaying = () => this.classList.add('is-playing');
      this.onPause = this.onPause.bind(this);
      this.onClick = this.onClick.bind(this);
      this.update = this.update.bind(this);
    }

    // The theme moves slides on variant change: connected / disconnected run
    // again each time, so every listener added here is removed below.
    connectedCallback() {
      this.video = this.querySelector('video');
      if (!this.video) return;
      this.bar = this.querySelector('.rl-video-progress');

      this.video.addEventListener('timeupdate', this.onTime);
      this.video.addEventListener('playing', this.onPlaying);
      this.video.addEventListener('pause', this.onPause);
      this.addEventListener('click', this.onClick);
      document.addEventListener('rl-video-story:toggle', this.update);

      this.observer = new IntersectionObserver(
        ([entry]) => {
          this.visible = entry.isIntersecting;
          this.update();
        },
        { threshold: 0.6 }
      );
      this.observer.observe(this);
      syncControls(this, this.video.muted, this.userPaused);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      document.removeEventListener('rl-video-story:toggle', this.update);
      this.removeEventListener('click', this.onClick);
      if (!this.video) return;
      this.video.removeEventListener('timeupdate', this.onTime);
      this.video.removeEventListener('playing', this.onPlaying);
      this.video.removeEventListener('pause', this.onPause);
      // No pause event on a paused video: the flag would swallow the next one.
      if (!this.video.paused) {
        this.selfPause = true;
        this.video.pause();
      }
    }

    // The theme's pauseAllMedia() (thumbnail click, variant change) stops
    // every video: resume when the slide is still the one on screen.
    onPause() {
      if (this.selfPause) {
        this.selfPause = false;
        return;
      }
      requestAnimationFrame(() => {
        if (this.isConnected && this.offsetParent && this.visible) this.update();
      });
    }

    onClick(event) {
      if (event.target.closest('[data-rl-video-mute]')) {
        this.video.muted = !this.video.muted;
        if (!this.video.muted) this.userPaused = false;
      } else if (event.target.closest('[data-rl-video-pause]')) {
        this.userPaused = !this.userPaused;
      } else {
        return;
      }
      this.update();
      syncControls(this, this.video.muted, this.userPaused);
    }

    update() {
      if (this.visible && !this.userPaused && !storyOpen) {
        // Light version on phones only (our 768 × 960 encode, else Shopify's
        // 720p); the desktop frame, ~1024 px on Retina, takes the high one.
        play(this.video, mobile.matches);
      } else if (!this.video.paused) {
        this.selfPause = true;
        this.video.pause();
      }
    }
  }

  class RlGalleryNav extends HTMLElement {
    connectedCallback() {
      this.gallery = this.closest('media-gallery');
      this.list = this.gallery?.querySelector('[id^="Slider-Gallery"]');
      this.dots = this.querySelector('.rl-gallery-nav__dots');
      if (!this.list) return;

      this.onClick = this.onClick.bind(this);
      this.onScroll = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.update();
        });
      };
      this.addEventListener('click', this.onClick);
      this.list.addEventListener('scroll', this.onScroll, { passive: true });
      this.observer = new MutationObserver((records) => {
        if (records.some((record) => record.type === 'childList')) this.render();
        else this.followActive();
      });
      this.observer.observe(this.list, { childList: true, attributes: true, attributeFilter: ['class'], subtree: true });
      this.onBreakpoint = () => (mobile.matches ? this.initSwiper() : this.destroySwiper());
      mobile.addEventListener('change', this.onBreakpoint);
      // The theme's media-gallery may be defined after this script runs.
      if (this.gallery.hasAttribute('data-rl-keep-scroll')) {
        customElements.whenDefined('media-gallery').then(() => this.keepScrollOnVariant());
      }
      this.initSwiper();
      this.render();
    }

    // Phones (setting rl_variant_keep_scroll): choosing a variant that keeps the
    // same photo (a format, a pack size) leaves the page where it is. The
    // theme's setActiveMedia scrolls the window up to the gallery (a smooth
    // scrollTo in a timeout it queues) whenever that photo sits above the
    // screen, and tells the sticky header to stay hidden on the next scroll. A
    // variant with another photo (a colour) still scrolls up to show it.
    keepScrollOnVariant() {
      const gallery = this.gallery;
      if (gallery.rlKeepScroll || typeof gallery.setActiveMedia !== 'function') return;
      gallery.rlKeepScroll = true;
      const setActiveMedia = gallery.setActiveMedia;
      const preventStickyHeader = gallery.preventStickyHeader;
      // The previous variant's photo: by the time the theme calls this, it has
      // already swapped the slides in, the new photo marked active.
      let shown = gallery.querySelector('.product__media-item.is-active')?.dataset.mediaId;
      // window.scrollTo minus smooth scrolls, installed for one variant change.
      // Two quick changes share it: the count restores the original only once
      // the last one is done, and only if nothing replaced it meanwhile.
      const nativeScrollTo = window.scrollTo;
      const keepScrollTo = function (options, ...rest) {
        if (options && typeof options === 'object' && options.behavior === 'smooth') return;
        return nativeScrollTo.call(window, options, ...rest);
      };
      let pending = 0;
      gallery.setActiveMedia = function (mediaId, prepend) {
        const samePhoto = Boolean(shown) && shown === mediaId;
        shown = mediaId;
        if (!mobile.matches || !samePhoto) return setActiveMedia.call(this, mediaId, prepend);
        pending += 1;
        window.scrollTo = keepScrollTo;
        this.preventStickyHeader = () => {};
        try {
          return setActiveMedia.call(this, mediaId, prepend);
        } finally {
          this.preventStickyHeader = preventStickyHeader;
          // Queued after the theme's timeout, so it runs once that one is done.
          setTimeout(() => {
            pending -= 1;
            if (!pending && window.scrollTo === keepScrollTo) window.scrollTo = nativeScrollTo;
          });
        }
      };
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.list?.removeEventListener('scroll', this.onScroll);
      mobile.removeEventListener('change', this.onBreakpoint);
      this.destroySwiper();
    }

    // Phones: Swiper moves the slides with a transform and leaves the vertical
    // pan to the page (touch-action: pan-y). The theme's native horizontal
    // scroller made iOS hesitate between the gallery and the page, above all
    // when reversing a fast vertical scroll. Swiper loads deferred: until it
    // is there, the native scroller works.
    initSwiper() {
      if (this.swiper || !mobile.matches || typeof window.Swiper !== 'function') return;
      const width = this.list.offsetWidth || 1;
      const start = Math.round(this.list.scrollLeft / width);
      this.list.scrollLeft = 0;
      this.gallery.classList.add('rl-media-gallery--swiper');
      this.swiper = new window.Swiper(this.list.parentElement, {
        wrapperClass: 'product__media-list',
        slideClass: 'product__media-item',
        initialSlide: start,
        slidesPerView: 1,
        // Same feel as Typology's product gallery (Swiper slide effect).
        speed: 300,
        threshold: 5,
        longSwipesRatio: 0.5,
        resistanceRatio: 0.85,
        on: {
          slideChange: () => {
            this.update();
            this.preloadNext();
          },
        },
      });
      this.preloadNext();
    }

    // The next video ahead of the slide shown starts loading (light version on
    // phones), however many photos lie between, so a swipe or a click to it
    // plays at once instead of waiting for the file. One video ahead only, and
    // not with Save-Data or on 2G / 3G, to spare a phone plan.
    preloadNext() {
      const connection = navigator.connection;
      if (connection?.saveData || ['slow-2g', '2g', '3g'].includes(connection?.effectiveType)) return;
      const slides = this.slides();
      const index = this.swiper ? this.swiper.activeIndex : this.activeIndex(slides);
      // Phones: slides of another variant are hidden. The desktop frame hides
      // every slide but the one shown.
      const ahead = slides.slice(index + 1).filter((slide) => !this.swiper || slide.getClientRects().length);
      const video = ahead.map((slide) => slide.querySelector('rl-gallery-video video')).find(Boolean);
      if (!video || video === this.preloading) return;
      // Before the page load, only once the visitor has moved on and the video
      // is on the next slide, one swipe away; otherwise it waits for the load,
      // the first photo (the LCP) keeping the connection.
      const swipeAway = index > 0 && ahead[0]?.contains(video);
      if (!swipeAway && document.readyState !== 'complete') {
        if (!this.waitingLoad) {
          this.waitingLoad = true;
          window.addEventListener('load', () => setTimeout(() => this.preloadNext(), 500), { once: true });
        }
        return;
      }
      this.preloading = video;
      const load = () => {
        const now = this.swiper ? this.swiper.activeIndex : this.activeIndex(this.slides());
        if (now === index && !fetchAhead(video, mobile.matches)) loadSource(video, 'auto', mobile.matches);
      };
      // The video shown, if any, gets the connection first: the next one
      // loads once it plays.
      const shown = slides[index]?.querySelector('rl-gallery-video video');
      if (shown && (shown.paused || shown.readyState < 3)) shown.addEventListener('playing', load, { once: true });
      else load();
    }

    destroySwiper() {
      if (!this.swiper) return;
      const index = this.swiper.activeIndex;
      this.swiper.destroy(true, true);
      this.swiper = null;
      this.gallery?.classList.remove('rl-media-gallery--swiper');
      const slide = this.slides()[index];
      if (slide && mobile.matches) this.list.scrollLeft = slide.offsetLeft;
    }

    // The theme marks the slide to show with .is-active (variant change,
    // mobile thumbnails) and scrolls the list to it: with Swiper, slide to it.
    followActive() {
      if (this.swiper) {
        const index = this.slides().findIndex((slide) => slide.classList.contains('is-active'));
        if (index >= 0 && index !== this.followed) {
          this.followed = index;
          this.swiper.slideTo(index, 0);
        }
      }
      this.update();
    }

    slides() {
      return Array.from(this.list.querySelectorAll(':scope > [data-media-id]'));
    }

    activeIndex(slides) {
      if (this.swiper) return this.swiper.activeIndex;
      if (mobile.matches) {
        const width = slides[0]?.offsetWidth || 1;
        return Math.min(slides.length - 1, Math.round(this.list.scrollLeft / width));
      }
      return Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    }

    render() {
      const slides = this.slides();
      // Videos read « Vidéo [index] / [count] » (token template of the section), photos « 3 / 16 ».
      const videoTemplate = this.dataset.labelVideo || '[index] / [count]';
      this.dots.replaceChildren(
        ...slides.map((slide, index) => {
          const isVideo = !!slide.querySelector('rl-gallery-video, .media-type-video, .media-type-external_video');
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'rl-gallery-nav__dot' + (isVideo ? ' rl-gallery-nav__dot--video' : '');
          button.dataset.rlGalleryGo = String(index);
          const template = isVideo ? videoTemplate : '[index] / [count]';
          button.setAttribute('aria-label', template.replace('[index]', index + 1).replace('[count]', slides.length));
          const mark = document.createElement('span');
          mark.className = 'rl-gallery-nav__mark';
          mark.setAttribute('aria-hidden', 'true');
          button.append(mark);
          return button;
        })
      );
      this.dots.style.setProperty('--rl-dots-count', slides.length);
      updateChips();
      if (this.swiper) {
        // Slides moved or replaced by the theme (variant change).
        this.swiper.update();
        this.followed = null;
        this.followActive();
        return;
      }
      this.update();
    }

    update() {
      const slides = this.slides();
      const active = this.activeIndex(slides);
      this.dots.querySelectorAll('[data-rl-gallery-go]').forEach((dot, index) => {
        if (index === active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      const prev = this.querySelector('[data-rl-gallery-step="-1"]');
      const next = this.querySelector('[data-rl-gallery-step="1"]');
      if (prev) prev.disabled = active <= 0;
      if (next) next.disabled = active >= slides.length - 1;
      // Desktop frame: the next video loads ahead too (high version); phones
      // do it on Swiper's slide change.
      if (!this.swiper) this.preloadNext();
    }

    go(index) {
      const slides = this.slides();
      const slide = slides[Math.max(0, Math.min(slides.length - 1, index))];
      if (!slide) return;
      if (this.swiper) {
        this.swiper.slideTo(slides.indexOf(slide), reducedMotion.matches ? 0 : undefined);
      } else if (mobile.matches) {
        this.list.scrollTo({ left: slide.offsetLeft, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      } else if (typeof this.gallery.setActiveMedia === 'function') {
        this.gallery.setActiveMedia(slide.dataset.mediaId, false);
      }
      this.update();
    }

    onClick(event) {
      const dot = event.target.closest('[data-rl-gallery-go]');
      const step = event.target.closest('[data-rl-gallery-step]');
      if (dot) this.go(Number(dot.dataset.rlGalleryGo));
      else if (step) this.go(this.activeIndex(this.slides()) + Number(step.dataset.rlGalleryStep));
    }
  }

  class RlVideoStory extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('.rl-video-story__dialog');
      this.stage = this.querySelector('.rl-video-story__stage');
      this.cube = this.querySelector('.rl-video-story__cube');
      this.track = this.querySelector('.rl-video-story__track');
      this.items = Array.from(this.querySelectorAll('.rl-video-story__item'));
      this.segments = Array.from(this.querySelectorAll('.rl-video-story__segment'));
      this.playlist = Array.from(this.querySelectorAll('[data-rl-story-go]'));
      this.parking = document.createDocumentFragment();
      this.index = 0;
      this.muted = false;
      this.paused = false;

      this.onClick = this.onClick.bind(this);
      this.onKey = this.onKey.bind(this);
      this.onWheel = this.onWheel.bind(this);
      this.onStageClick = this.onStageClick.bind(this);

      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKey);
      this.dialog.addEventListener('wheel', this.onWheel, { passive: true });
      this.cube.addEventListener('click', this.onStageClick);
      // Leaving full screen (Android back button / gesture) closes the player.
      document.addEventListener('fullscreenchange', () => {
        if (document.fullscreenElement !== this && this.fullscreen) {
          this.fullscreen = false;
          if (!this.hidden) this.close();
        }
      });

      this.items.forEach((item, index) => {
        const video = item.querySelector('video');
        video.loop = false;
        video.addEventListener('timeupdate', () => {
          if (index !== this.index) return;
          setProgress(this.segments[index]?.firstElementChild, video.currentTime / video.duration);
          this.querySelector('[data-rl-story-time]').textContent =
            formatTime(video.currentTime) + ' / ' + (item.dataset.duration || formatTime(video.duration));
        });
        video.addEventListener('ended', () => {
          if (index === this.index) this.step(1, true);
        });
      });
    }

    current() {
      return this.items[this.index]?.querySelector('video');
    }

    // The videos the gallery shows (only the selected variant's with the
    // « variant media only » setting), all of them when it shows none. The
    // others leave the track (Swiper counts every slide in it).
    applyOrder() {
      const shown = new Set(galleryVideoIds());
      this.order = this.items.map((item, index) => index).filter((index) => shown.has(this.items[index].dataset.mediaId));
      if (!this.order.length) this.order = this.items.map((item, index) => index);
      const active = new Set(this.order);
      this.items.forEach((item, index) => (active.has(index) ? this.track : this.parking).append(item));
      this.segments.forEach((segment, index) => (segment.hidden = !active.has(index)));
      this.playlist.forEach((button) => (button.hidden = !active.has(Number(button.dataset.rlStoryGo))));
      this.querySelectorAll('[data-rl-story-count]').forEach((element) => (element.textContent = this.order.length));
    }

    // Instagram stories: horizontal swipe with Swiper's cube effect. Built on
    // the first opening (the player must be visible to be measured).
    mountSwiper() {
      if (typeof window.Swiper !== 'function') return;
      if (this.swiper) {
        this.swiper.update();
        return;
      }
      this.classList.add('rl-video-story--swiper');
      this.swiper = new window.Swiper(this.cube, {
        wrapperClass: 'rl-video-story__track',
        slideClass: 'rl-video-story__item',
        effect: reducedMotion.matches ? 'slide' : 'cube',
        cubeEffect: { shadow: false, slideShadows: true },
        speed: reducedMotion.matches ? 0 : 450,
        threshold: 6,
        longSwipesRatio: 0.2,
        resistanceRatio: 0,
        on: {
          slideChange: (swiper) => this.activate(this.order[swiper.activeIndex]),
          tap: (swiper, event) => this.onTap(event),
          touchStart: () => this.holdStart(),
          sliderMove: () => this.holdCancel(),
          touchEnd: () => this.holdEnd(),
        },
      });
    }

    open(mediaId, opener, stories, menuCleared) {
      if (!stories && iOS && this.dataset.iosMode === 'native' && this.playNative(mediaId, opener)) return;
      if (!menuCleared && this.hideMenuFirst(() => this.open(mediaId, opener, stories, true))) return;
      this.opener = opener;
      this.applyOrder();
      const found = this.items.findIndex(
        (item, index) => item.dataset.mediaId === String(mediaId) && this.order.includes(index)
      );
      this.hidden = false;
      storyOpen = true;
      document.body.classList.add('overflow-hidden');
      this.claimScreen(true);
      document.dispatchEvent(new CustomEvent('rl-video-story:toggle'));
      this.muted = false;
      this.paused = false;
      this.syncCta();
      this.mountSwiper();
      this.show(found >= 0 ? found : this.order[0], true);
      this.dialog.focus();
      this.enterFullscreen();
    }

    // iOS: opened over the shown sticky menu, the player leaves Safari's bar
    // white whatever it does; opened once the menu is hidden, the bar turns
    // black. So the menu slides away first, as the theme does on scroll down
    // (its hidden class, no page move), and the player opens once it is off
    // screen (~0.15 s); the menu comes back on the next scroll up.
    hideMenuFirst(then) {
      const header = document.querySelector('.section-header');
      if (
        !iOS ||
        !header ||
        !header.classList.contains('shopify-section-header-sticky') ||
        header.classList.contains('shopify-section-header-hidden') ||
        header.getBoundingClientRect().bottom <= 0
      ) {
        return false;
      }
      let done = false;
      const go = () => {
        if (done) return;
        done = true;
        header.removeEventListener('transitionend', onEnd);
        setTimeout(then, 60);
      };
      const onEnd = (event) => {
        if (event.target === header) go();
      };
      header.addEventListener('transitionend', onEnd);
      header.classList.add('shopify-section-header-hidden');
      setTimeout(go, 260);
      return true;
    }

    // Touch devices other than iOS (Android): true full screen, browser bars
    // hidden. Called within the opening tap, as the Fullscreen API requires.
    enterFullscreen() {
      if (iOS || !touch.matches || !this.requestFullscreen || document.fullscreenElement) return;
      this.requestFullscreen({ navigationUI: 'hide' })
        .then(() => (this.fullscreen = true))
        .catch(() => {});
    }

    // « Player on iPhone: Native full screen »: iOS only gives full screen to
    // a single <video>. A video without playsinline started within the tap
    // opens iOS's own full-screen player; it is removed when that one closes.
    playNative(mediaId, opener) {
      const item = this.items.find((element) => element.dataset.mediaId === String(mediaId)) || this.items[0];
      const source = item?.querySelector('video');
      const src = (source && encodedSource(source, mobile.matches || networkSpeed() === 'slow')) || source?.dataset.src || source?.currentSrc || source?.getAttribute('src');
      if (!src) return false;
      const video = document.createElement('video');
      video.className = 'rl-video-story__native';
      video.src = src;
      // Our encode unreachable (CDN down): the Shopify file, once.
      if (source?.dataset.src && src !== source.dataset.src) {
        video.addEventListener('error', () => (video.src = source.dataset.src), { once: true });
      }
      video.controls = true;
      video.setAttribute('aria-label', item.dataset.title || '');
      const done = () => {
        video.pause();
        video.remove();
      };
      video.addEventListener('webkitendfullscreen', done, { once: true });
      // Safety net: if iOS did not go full screen, no hidden video playing
      // with sound — the stories player opens instead.
      video.addEventListener(
        'playing',
        () => {
          setTimeout(() => {
            if (video.isConnected && !video.webkitDisplayingFullscreen) {
              done();
              this.open(mediaId, opener, true);
            }
          }, 800);
        },
        { once: true }
      );
      document.body.append(video);
      video.play().catch(() => {
        done();
        this.open(mediaId, opener, true);
      });
      return true;
    }

    close() {
      this.holdCancel();
      if (document.fullscreenElement === this) {
        this.fullscreen = false;
        document.exitFullscreen().catch(() => {});
      }
      this.items.forEach((item) => item.querySelector('video').pause());
      this.hidden = true;
      storyOpen = false;
      document.body.classList.remove('overflow-hidden');
      this.claimScreen(false);
      document.dispatchEvent(new CustomEvent('rl-video-story:toggle'));
      this.opener?.focus();
    }

    // While open, the player takes the whole screen (viewport-fit=cover: the
    // safe-area insets of rl-video-story.css keep progress, close and the
    // bottom band clear of the status bar and home indicator). Safari keeps
    // its status bar area for itself above a fixed layer and fills it with a
    // colour it samples from the page: the page background and theme-color
    // turn black, and a 1 px scroll back and forth makes it sample again, so
    // the area is always black while open (white again on close).
    claimScreen(open) {
      // Safari samples fixed / sticky elements at the top (the sticky header,
      // white) and the page background: the header is hidden while open
      // (rl-story-open, rl-video-story.css) and html / body turn black.
      const root = document.documentElement;
      root.classList.toggle('rl-story-open', open);
      if (open) {
        this.savedRootBackground = root.style.backgroundColor;
        this.savedBodyBackground = document.body.style.backgroundColor;
        root.style.backgroundColor = '#000';
        document.body.style.backgroundColor = '#000';
      } else {
        root.style.backgroundColor = this.savedRootBackground || '';
        document.body.style.backgroundColor = this.savedBodyBackground || '';
      }
      // Same mechanics as the gallery, where Safari does re-sample: a real
      // 1 px scroll over two frames (two moves in one frame merge into none),
      // and the off-screen header back to visible one frame later; again
      // once the layout has settled.
      const y = window.scrollY;
      const header = document.querySelector('.section-header');
      const nudge = () => {
        window.scrollTo(0, y > 0 ? y - 1 : y + 1);
        if (header && open) header.style.visibility = 'visible';
        requestAnimationFrame(() => {
          window.scrollTo(0, y);
          if (header) header.style.visibility = '';
        });
      };
      requestAnimationFrame(nudge);
      setTimeout(nudge, 120);
      const viewport = document.querySelector('meta[name="viewport"]');
      let theme = document.querySelector('meta[name="theme-color"]');
      if (open) {
        this.savedViewport = viewport?.getAttribute('content');
        if (viewport && !/viewport-fit/.test(this.savedViewport || '')) {
          viewport.setAttribute('content', (this.savedViewport ? this.savedViewport + ', ' : '') + 'viewport-fit=cover');
        }
        if (!theme) {
          theme = document.createElement('meta');
          theme.name = 'theme-color';
          document.head.append(theme);
          this.addedTheme = theme;
        }
        this.savedTheme = theme.getAttribute('content');
        theme.setAttribute('content', '#000000');
        return;
      }
      if (viewport && this.savedViewport != null) viewport.setAttribute('content', this.savedViewport);
      if (this.addedTheme) {
        this.addedTheme.remove();
        this.addedTheme = null;
      } else if (theme) theme.setAttribute('content', this.savedTheme || '');
    }

    // index: position of the video in the full list; pos: rank among the shown
    // ones (= Swiper's slide). With Swiper the slide change runs activate().
    show(index, instant) {
      const pos = this.order.indexOf(index);
      if (pos < 0) return;
      if (this.swiper && this.swiper.activeIndex !== pos) {
        this.swiper.slideTo(pos, instant ? 0 : undefined);
        return;
      }
      this.activate(index);
    }

    activate(index) {
      const pos = this.order.indexOf(index);
      if (pos < 0) return;
      const previous = this.current();
      if (previous && index !== this.index) {
        previous.pause();
        previous.currentTime = 0;
      }
      this.index = index;
      this.track.style.setProperty('--rl-story-index', pos);
      this.segments.forEach((segment, k) => setProgress(segment.firstElementChild, this.order.indexOf(k) < pos ? 1 : 0));
      this.playlist.forEach((button, k) => {
        if (k === index) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });

      const item = this.items[index];
      const next = this.items[this.order[(pos + 1) % this.order.length]];
      this.querySelector('[data-rl-story-index]').textContent = pos + 1;
      this.querySelector('[data-rl-story-time]').textContent = '0:00 / ' + (item.dataset.duration || '');
      this.querySelector('[data-rl-story-title]').textContent = item.dataset.title;
      const prevButton = this.querySelector('.rl-video-story__nav [data-rl-story-step="-1"]');
      if (prevButton) prevButton.disabled = pos === 0;

      // Stories left half-loaded stop downloading, so that the one shown gets
      // the whole connection (a fast tap through stories stalled on 4G); one
      // fully loaded keeps its file for a way back.
      this.items.forEach((other) => {
        const video = other.querySelector('video');
        if (other === item || other === next) return;
        if (!video?.getAttribute('src')) return dropPrefetch(video);
        const buffered = video.buffered.length ? video.buffered.end(video.buffered.length - 1) : 0;
        if (video.duration && buffered >= video.duration - 0.5) return;
        if (video.src.startsWith('blob:')) URL.revokeObjectURL(video.src);
        video.removeAttribute('src');
        video.load();
      });

      this.playCurrent();
      // The next one starts loading once this one plays, not alongside it.
      if (next !== item) {
        const current = item.querySelector('video');
        const preloadNext = () => {
          if (this.index !== index) return;
          const nextVideo = next.querySelector('video');
          if (!fetchAhead(nextVideo, mobile.matches)) loadSource(nextVideo, 'metadata', mobile.matches);
        };
        if (!current.paused && current.readyState >= 3) preloadNext();
        else current.addEventListener('playing', preloadNext, { once: true });
      }
    }

    playCurrent() {
      const video = this.current();
      video.muted = this.muted;
      if (this.paused) video.pause();
      else
        play(video, mobile.matches).then(() => {
          // Unmuted playback needs a user gesture (iOS: per element); fall back to muted.
          if (video === this.current() && video.paused && !this.paused && !video.muted) {
            this.muted = true;
            video.muted = true;
            play(video, mobile.matches);
            syncControls(this, this.muted, this.paused);
          }
        });
      syncControls(this, this.muted, this.paused);
    }

    // Mirrors the price, availability and shade of the variant selected on the page.
    syncCta() {
      const cta = this.querySelector('[data-rl-story-cta]');
      if (!cta) return;
      const form = document.getElementById(this.dataset.productForm);
      const submit = form?.querySelector('[type="submit"]');
      cta.hidden = !submit || submit.disabled;
      const sectionId = this.id.replace('RlVideoStory-', '');
      const box = document.getElementById('price-' + sectionId);
      const price = box?.querySelector('.price--on-sale .price-item--sale') || box?.querySelector('.price-item--regular');
      const target = cta.querySelector('[data-rl-story-price]');
      if (price && target && price.textContent.trim()) target.textContent = price.textContent.trim();
      // The shade picked on the page (« Card title · Shade », bottom band on phones).
      const shade = document
        .getElementById('shopify-section-' + sectionId)
        ?.querySelector('.product-form__input--swatch [data-selected-value]');
      const shadeTarget = this.querySelector('[data-rl-story-shade]');
      if (shade && shadeTarget && shade.textContent.trim()) shadeTarget.textContent = shade.textContent.trim();
    }

    onClick(event) {
      const target = event.target;
      if (target.closest('[data-rl-story-close]')) return this.close();
      const go = target.closest('[data-rl-story-go]');
      if (go) return this.show(Number(go.dataset.rlStoryGo));
      const step = target.closest('[data-rl-story-step]');
      if (step) return this.step(Number(step.dataset.rlStoryStep), true);
      if (target.closest('[data-rl-video-mute]')) {
        this.muted = !this.muted;
        this.current().muted = this.muted;
        return syncControls(this, this.muted, this.paused);
      }
      if (target.closest('[data-rl-video-pause]')) {
        this.paused = !this.paused;
        return this.playCurrent();
      }
      if (target.closest('[data-rl-story-cta]')) {
        const form = document.getElementById(this.dataset.productForm);
        const submit = form?.querySelector('[type="submit"]');
        if (!submit || submit.disabled) return;
        // The cart drawer / notification of the theme confirms the add.
        this.close();
        submit.click();
      }
    }

    // Stories: the left third goes back (on the first video it restarts it),
    // the rest goes on (after the last one, the first).
    onTap(event) {
      if (this.ignoreTap) {
        this.ignoreTap = false;
        return;
      }
      const rect = this.cube.getBoundingClientRect();
      const x = (event?.clientX ?? event?.changedTouches?.[0]?.clientX ?? rect.right) - rect.left;
      if (x < rect.width / 3) {
        if (this.order.indexOf(this.index) === 0) {
          this.current().currentTime = 0;
          this.playCurrent();
        } else this.step(-1);
      } else this.step(1, true);
    }

    // Without Swiper, a click on the video does what a tap does.
    onStageClick(event) {
      if (!this.swiper) this.onTap(event);
    }

    // Press and hold pauses, as in stories; letting go resumes.
    holdStart() {
      this.holdCancel();
      this.holdTimer = setTimeout(() => {
        this.holding = true;
        this.current()?.pause();
      }, 250);
    }

    holdCancel() {
      clearTimeout(this.holdTimer);
    }

    holdEnd() {
      this.holdCancel();
      if (!this.holding) return;
      this.holding = false;
      this.ignoreTap = true;
      setTimeout(() => (this.ignoreTap = false), 400);
      if (!this.paused) this.playCurrent();
    }

    step(delta, wrap) {
      const count = this.order.length;
      let pos = this.order.indexOf(this.index) + delta;
      if (wrap) pos = (pos + count) % count;
      if (pos >= 0 && pos < count) this.show(this.order[pos]);
    }

    onKey(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      } else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        event.preventDefault();
        this.step(1);
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        event.preventDefault();
        this.step(-1);
      } else if (event.key === 'Tab') {
        const focusable = Array.from(this.querySelectorAll('button:not([disabled]):not([hidden])')).filter(
          (element) => element.offsetParent !== null
        );
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && (document.activeElement === first || document.activeElement === this.dialog)) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    }

    // One video per gesture: a mouse wheel sends a few large deltas, a trackpad
    // a stream of small ones followed by inertia (either axis). Deltas add up
    // until 40 px, then the wheel stays locked until the stream stops for 250 ms.
    onWheel(event) {
      clearTimeout(this.wheelTimer);
      this.wheelTimer = setTimeout(() => {
        this.wheelLocked = false;
        this.wheelSum = 0;
      }, 250);
      if (this.wheelLocked) return;
      const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
      this.wheelSum = (this.wheelSum || 0) + delta;
      if (Math.abs(this.wheelSum) < 40) return;
      this.wheelLocked = true;
      this.step(this.wheelSum > 0 ? 1 : -1);
      this.wheelSum = 0;
    }
  }

  // iOS Safari tints its status bar with the colour of a sticky element at the
  // top (the white sticky header) and keeps that choice: the gallery photo
  // then never shows behind the clock, until something makes Safari look at
  // the top again (as closing the player did). When the theme hides the
  // header on scroll down, as soon as its slide-up is over and it is off
  // screen, it is hidden for one frame more, which makes Safari sample the
  // top again: no header there, so the page shows behind its bar. (Mid-slide
  // it flashed and Safari sampled the white header still there.)
  const header = iOS && document.querySelector('.section-header');
  if (header) {
    const isHidden = () => header.classList.contains('shopify-section-header-hidden');
    let wasHidden = isHidden();
    let pending = false;
    let timer = null;
    const resample = () => {
      clearTimeout(timer);
      if (!pending || !isHidden()) return;
      pending = false;
      header.style.visibility = 'hidden';
      requestAnimationFrame(() => requestAnimationFrame(() => (header.style.visibility = '')));
    };
    // Right when the slide-up ends, so the bar clears with the header (one
    // step); a fallback for a hide without animation.
    header.addEventListener('transitionend', (event) => {
      // The theme slides the header with « top » (0.15 s).
      if (event.target === header && (event.propertyName === 'top' || event.propertyName === 'transform')) resample();
    });
    new MutationObserver(() => {
      const hidden = isHidden();
      if (hidden && !wasHidden) {
        pending = true;
        clearTimeout(timer);
        timer = setTimeout(resample, 300);
      } else if (!hidden) {
        pending = false;
        clearTimeout(timer);
      }
      wasHidden = hidden;
    }).observe(header, { attributes: true, attributeFilter: ['class'] });
  }

  // The frame's height cap (--rl-media-cap) needs the gallery's real top:
  // announcement bar + header + breadcrumb, which the theme guesses at 170 px.
  const measureGalleryTop = () => {
    const gallery = document.querySelector('media-gallery.rl-media-gallery');
    if (!gallery) return;
    const top = Math.round(gallery.getBoundingClientRect().top + window.scrollY);
    document.documentElement.style.setProperty('--rl-media-top', top + 'px');
  };
  let measureFrame = null;
  const scheduleMeasure = () => {
    if (measureFrame) return;
    measureFrame = requestAnimationFrame(() => {
      measureFrame = null;
      measureGalleryTop();
    });
  };
  measureGalleryTop();
  window.addEventListener('load', scheduleMeasure);
  window.addEventListener('resize', scheduleMeasure);

  customElements.define('rl-gallery-video', RlGalleryVideo);
  customElements.define('rl-gallery-nav', RlGalleryNav);
  customElements.define('rl-video-story', RlVideoStory);

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-rl-story-open]');
    if (!opener) return;
    const story = document.getElementById(opener.getAttribute('aria-controls'));
    if (!story || typeof story.open !== 'function') return;
    event.preventDefault();
    story.open(opener.dataset.rlStoryOpen, opener);
  });
})();
